import { useState } from 'react';
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { useJournalAcces } from '@/hooks/useTerrainAdmin';
import type { JournalEntry } from '@/api/terrain-admin';
import { Badge, type BadgeTone } from './_ui';

/**
 * SIMG-TERRAIN-2026-001 — Page "Journal d'accès".
 *
 * Audit des tentatives d'ouverture de session terrain, paginé, avec filtres.
 * Utile pour diagnostiquer :
 *   - "Le portail me refuse mon lien" → résultat=appareil_different ou expire
 *   - "Un lien a été utilisé par quelqu'un d'autre" → résultat=appareil_different
 *   - Suivi général de l'usage
 */

const LIMIT = 50;

const LIBELLES_RESULTAT: Record<string, { label: string; tone: BadgeTone }> = {
  ok:                 { label: 'Connexion OK',          tone: 'success' },
  expire:             { label: 'Lien expiré',           tone: 'warning' },
  revoque:            { label: 'Lien révoqué',          tone: 'neutral' },
  appareil_different: { label: 'Autre appareil (refus)', tone: 'danger'  },
  inconnu:            { label: 'Lien inconnu',          tone: 'danger'  },
};

export const JournalPage = () => {
  const [offset, setOffset]     = useState(0);
  const [filtre, setFiltre]     = useState('');
  const [depuis, setDepuis]     = useState('');
  const { data, isLoading, isFetching, refetch } = useJournalAcces({
    limit:    LIMIT,
    offset,
    resultat: filtre || undefined,
    depuis:   depuis ? new Date(depuis).toISOString() : undefined,
  });

  const total   = data?.total ?? 0;
  const pageMax = Math.max(0, Math.ceil(total / LIMIT) - 1);
  const pageCur = Math.floor(offset / LIMIT);

  return (
    <>
      <Topbar
        title="Journal d'accès terrain"
        subtitle="Toutes les tentatives d'ouverture de session sur les liens intervenants"
        actions={
          <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}>
            <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
          </button>
        }
      />
      <div className="ds-content">
        <div className="ds-panel" style={{ padding: 12, marginBottom: 12, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 4 }}>Résultat</div>
            <select
              value={filtre}
              onChange={(e) => { setFiltre(e.target.value); setOffset(0); }}
              style={{ padding: '6px 10px', border: '1px solid var(--bd-2)', borderRadius: 6, fontSize: 13, background: 'var(--bg-1)' }}
            >
              <option value="">Tous</option>
              {Object.entries(LIBELLES_RESULTAT).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </label>
          <label>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 4 }}>Depuis</div>
            <input
              type="date"
              value={depuis}
              onChange={(e) => { setDepuis(e.target.value); setOffset(0); }}
              style={{ padding: '6px 10px', border: '1px solid var(--bd-2)', borderRadius: 6, fontSize: 13, background: 'var(--bg-1)' }}
            />
          </label>
          <div style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--tx-2)' }}>
            <strong>{total}</strong> entrée{total > 1 ? 's' : ''} au total
          </div>
        </div>

        <div className="ds-panel" style={{ padding: 0, overflow: 'hidden' }}>
          {isLoading && <div style={{ padding: 24, textAlign: 'center', color: 'var(--tx-3)' }}>Chargement…</div>}
          {!isLoading && data && data.rows.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--tx-3)' }}>Aucune entrée sur cette page.</div>
          )}
          {data && data.rows.length > 0 && (
            <table className="ds-tbl">
              <thead>
                <tr>
                  <th style={{ width: 160 }}>Horodatage</th>
                  <th style={{ width: 200 }}>Résultat</th>
                  <th>Intervenant</th>
                  <th style={{ width: 130 }}>IP</th>
                  <th style={{ width: 140 }}>Empreinte</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => <JournalRow key={r.id} row={r} />)}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {total > LIMIT && (
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12 }}>
            <button
              className="ds-btn ds-btn-g ds-btn-sm"
              onClick={() => setOffset(Math.max(0, offset - LIMIT))}
              disabled={offset === 0}
            >
              <ChevronLeft size={14} /> Précédent
            </button>
            <span style={{ fontSize: 13, color: 'var(--tx-2)' }}>
              Page <strong>{pageCur + 1}</strong> / {pageMax + 1}
            </span>
            <button
              className="ds-btn ds-btn-g ds-btn-sm"
              onClick={() => setOffset(offset + LIMIT)}
              disabled={pageCur >= pageMax}
            >
              Suivant <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>
    </>
  );
};

function JournalRow({ row }: { row: JournalEntry }) {
  const meta = LIBELLES_RESULTAT[row.resultat] ?? { label: row.resultat, tone: 'neutral' as const };
  return (
    <tr>
      <td style={{ fontSize: 12, color: 'var(--tx-2)' }}>
        {new Date(row.horodatage).toLocaleString('fr-FR')}
      </td>
      <td>
        <Badge tone={meta.tone}>{meta.label}</Badge>
      </td>
      <td>{row.intervenant?.nom ?? <span style={{ color: 'var(--tx-3)', fontStyle: 'italic' }}>—</span>}</td>
      <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--tx-3)' }}>
        {row.ip ?? '—'}
      </td>
      <td style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--tx-3)' }} title={row.appareilHash ?? ''}>
        {row.appareilHash ? row.appareilHash.slice(0, 12) + '…' : '—'}
      </td>
    </tr>
  );
}
